import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { FeatureFlag } from '@ndvr/auth-api';
import { Email, PhoneCallView, SmsMessage } from '@ndvr/customer-api';

import { CustomerApi } from 'API';
import { FetcherError } from 'utils';
import { useFeatureFlag } from 'views/auth/useFeatureFlag';
import { useCallsQuery } from 'views/household/HouseholdCalls/call-queries';
import { EmailQueryKeys, useEmailsQuery } from 'views/household/HouseholdEmails/email-queries';
import { TextQueryKeys, useTextsQuery } from 'views/household/HouseholdTexts/text-queries';
import { previewText } from 'views/household/HouseholdTexts/text-utils';
import { Toast } from 'widgets';

import { CallMessageRow, EmailMessageRow, MessageRow, TextMessageRow } from './message-types';

const toEmailRow = (email: Email, id: string): EmailMessageRow => ({
    type: 'email',
    id,
    mostRecentAt: email.sentAt,
    subject: email.subject || '',
    fromName: email.sender?.name || '',
    fromEmail: email.sender?.email || '',
    toName: email.recipient?.name || '',
    toEmail: email.recipient?.email || '',
    emailGUID: email.emailGUID,
});

export const buildEmailThreads = (emails: Email[]): EmailMessageRow[] => {
    const threadMap = new Map<string, Email[]>();
    emails.forEach((email) => {
        const key = email.threadId || email.emailGUID;
        if (!threadMap.has(key)) threadMap.set(key, []);
        threadMap.get(key)!.push(email);
    });

    return Array.from(threadMap.values()).map((group) => {
        const sorted = [...group].sort((a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime());
        const latest = sorted[0];
        const rest = sorted.slice(1);

        const row = toEmailRow(latest, latest.threadId || latest.emailGUID);
        if (rest.length > 0) {
            row.children = rest.map((email) => toEmailRow(email, email.emailGUID));
        }
        return row;
    });
};

export const buildCallRows = (calls: PhoneCallView[]): CallMessageRow[] =>
    calls.map((call) => ({
        type: 'call' as const,
        id: call.zoomPhoneCallGUID,
        mostRecentAt: call.startTime || '',
        call,
    }));

export const buildTextThreads = (texts: SmsMessage[]): TextMessageRow[] =>
    texts.map((text) => ({
        type: 'text' as const,
        id: text.smsMessageGUID,
        mostRecentAt: text.sentAt || '',
        preview: previewText(text),
        fromName: text.fromName || '',
        fromPhone: text.fromNumber || '',
        toName: text.toName || '',
        toPhone: text.toNumber || '',
        smsMessageGUID: text.smsMessageGUID,
    }));

export const useMessages = (householdGUID: string) => {
    const callsEnabled = useFeatureFlag(FeatureFlag.CC_PHONE_CALLS);
    const emailsQuery = useEmailsQuery(householdGUID);
    const textsQuery = useTextsQuery(householdGUID);
    const callsQuery = useCallsQuery(householdGUID, callsEnabled);

    const messages = useMemo((): MessageRow[] => {
        const emailThreads = buildEmailThreads(emailsQuery.data ?? []);
        const textThreads = buildTextThreads(textsQuery.data ?? []);
        const callRows = buildCallRows(callsQuery.data ?? []);
        return [...emailThreads, ...textThreads, ...callRows].sort(
            (a, b) => new Date(b.mostRecentAt).getTime() - new Date(a.mostRecentAt).getTime(),
        );
    }, [emailsQuery.data, textsQuery.data, callsQuery.data]);

    return {
        messages,
        isLoading: emailsQuery.isLoading || textsQuery.isLoading || callsQuery.isLoading,
        isError: emailsQuery.isError || textsQuery.isError || callsQuery.isError,
    };
};

export const useSyncMessages = (householdGUID: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async () => {
            await CustomerApi().importEmailsForHousehold(householdGUID);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: EmailQueryKeys.EmailsQueryKey(householdGUID) });
            queryClient.invalidateQueries({ queryKey: TextQueryKeys.TextsQueryKey(householdGUID) });
        },
        onError: (error) => {
            if (error instanceof FetcherError && error.httpStatus === 429) {
                Toast.info('Messages were recently synced — please wait a moment before trying again.');
            } else {
                Toast.forError(error, 'Failed to sync messages');
            }
        },
    });
};
